import { Bike, Sparkles } from "lucide-react";
import { BRAND } from "@/lib/brand";

const BRANCHES = [
  {
    key: "MC",
    est: BRAND.mcEst,
    icon: Bike,
    title: "Motorcycle Club",
    tag: "For the riders.",
    image: BRAND.images.mcBranch,
    body:
      "The original crown. Established 2020 for women who ride on 2s and 3s — from sportbikes to Harley trikes. From sunrise runs to Gulf Coast rides, the MC is where leather meets lipstick and throttle meets throne.",
    bullets: [
      "Licensed female riders — 2-wheel or 3-wheel (trikes welcome)",
      "Monthly rides, charity runs & out-of-state trips",
      "Full patch program with prospect & hang-around phases",
    ],
  },
  {
    key: "SC",
    est: BRAND.scEst,
    icon: Sparkles,
    title: "Social Club",
    tag: "For the supporters.",
    image: BRAND.images.scBranch,
    body:
      "Born in 2021 as the heartbeat off the road. The SC brings together women who rep the crown, ride passenger, or simply believe in this sisterhood — bonded by events, service, and pure joy.",
    bullets: [
      "Open to women (no riding required)",
      "Event-driven: brunches, galas, fundraisers",
      "Dedicated SC rocker & exclusive member-only events",
    ],
  },
];

export default function Branches() {
  return (
    <section
      id="branches"
      className="relative py-24 sm:py-32 border-t border-white/5 bg-[#080808]"
      data-testid="branches-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="max-w-3xl mb-20">
          <div className="text-xs uppercase tracking-[0.35em] text-brand mb-4 flex items-center gap-3">
            <span className="h-px w-10 bg-brand" /> The Branches
          </div>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-serif leading-[1.02]"
            data-testid="branches-title"
          >
            Two crowns.
            <br />
            <span className="italic text-white/80">One sisterhood.</span>
          </h2>
          <p className="mt-6 text-lg text-white/65 max-w-2xl">
            Whether you twist a throttle or twist a glass of champagne — there's
            a place for you in the Crown Jewels family.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {BRANCHES.map((b) => {
            const Icon = b.icon;
            return (
              <article
                key={b.key}
                className="group relative border border-white/10 hover:border-brand/60 bg-[#0a0a0a] transition-all duration-500 overflow-hidden"
                data-testid={`branch-card-${b.key}`}
              >
                <div className="relative h-72 overflow-hidden">
                  <img
                    src={b.image}
                    alt={b.title}
                    className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-[1200ms]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/40 to-transparent" />
                  <div className="absolute top-6 left-6 flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-12 h-12 border border-brand bg-[#050505]/70 backdrop-blur-sm">
                      <Icon className="w-5 h-5 text-brand" strokeWidth={1.5} />
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.3em] text-white/80 bg-black/60 px-3 py-1.5 border border-white/10">
                      Est. {b.est}
                    </span>
                  </div>
                  <div className="absolute bottom-6 right-6 font-script text-5xl text-brand leading-none drop-shadow-[0_0_20px_rgba(233,30,140,0.6)]">
                    {b.key}
                  </div>
                </div>

                <div className="p-8 lg:p-10 space-y-5">
                  <div>
                    <p className="font-script text-xl text-brand">{b.tag}</p>
                    <h3 className="text-3xl font-serif text-white mt-1">
                      {b.title}
                    </h3>
                  </div>
                  <p className="text-white/65 leading-relaxed">{b.body}</p>
                  <ul className="space-y-3 pt-2">
                    {b.bullets.map((item, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-3 text-sm text-white/75"
                        data-testid={`branch-${b.key}-bullet-${i}`}
                      >
                        <span className="mt-2 inline-block w-1.5 h-1.5 bg-brand shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
